import type { MeetingMode } from "../lib/api.ts";
import { MeetingBadge } from "./MeetingBadge.tsx";

interface ConfirmSummaryProps {
  serviceNames: string[];
  staffName: string | null;
  date: string; // "YYYY-MM-DD"
  time: string; // "HH:mm"
  durationMin: number;
  priceCents: number | null;
  currency: string;
  meetingMode: MeetingMode;
  onConfirm: () => void;
  onBack: () => void;
  loading?: boolean;
  error?: string | null;
}

function formatDateTime(dateStr: string, timeStr: string): string {
  // Build a local Date from "YYYY-MM-DD" + "HH:mm" to avoid UTC shift.
  const [y, m, d] = dateStr.split("-").map(Number);
  const [h, min] = timeStr.split(":").map(Number);
  if ([y, m, d, h, min].some(Number.isNaN)) return `${dateStr} at ${timeStr}`;
  const local = new Date(y!, m! - 1, d!, h, min);
  return local.toLocaleString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatPrice(cents: number | null, currency: string): string {
  if (cents === null || cents === 0) return "Free";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

interface SummaryRowProps {
  label: string;
  children: React.ReactNode;
}

function SummaryRow({ label, children }: SummaryRowProps) {
  return (
    <div className="p-4">
      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <div className="text-gray-900">{children}</div>
    </div>
  );
}

export function ConfirmSummary({
  serviceNames,
  staffName,
  date,
  time,
  durationMin,
  priceCents,
  currency,
  meetingMode,
  onConfirm,
  onBack,
  loading,
  error,
}: ConfirmSummaryProps) {
  return (
    <div>
      <div className="bg-white border border-gray-200 rounded-[var(--radius)] divide-y divide-gray-100 overflow-hidden">
        <SummaryRow label="Service">
          <p className="font-medium">{serviceNames.join(", ")}</p>
          <div className="mt-1">
            <MeetingBadge mode={meetingMode} />
          </div>
        </SummaryRow>

        <SummaryRow label="Date &amp; Time">
          <p className="font-medium">{formatDateTime(date, time)}</p>
          <p className="text-sm text-gray-500 mt-0.5">{durationMin} min</p>
        </SummaryRow>

        {staffName && (
          <SummaryRow label="Provider">
            <p className="font-medium">{staffName}</p>
          </SummaryRow>
        )}

        <SummaryRow label="Total">
          <p className="font-semibold text-lg">{formatPrice(priceCents, currency)}</p>
        </SummaryRow>
      </div>

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-[var(--radius)] text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="mt-6 flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 py-2.5 px-4 rounded-[var(--radius)] border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={loading}
          className="flex-1 py-2.5 px-4 rounded-[var(--radius)] text-white text-sm font-medium disabled:opacity-60 transition-opacity hover:opacity-90"
          style={{ backgroundColor: "var(--accent)" }}
        >
          {loading ? "Booking..." : "Confirm Booking"}
        </button>
      </div>
    </div>
  );
}
